/**
 * Shop Success Page - Order confirmed
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, Package, Loader2, Home, ShoppingBag } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { shopAPI } from '../../services/api';

const ShopSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('order_id');
  
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    const loadOrder = async () => {
      if (!orderId) {
        setLoading(false);
        return;
      }
      
      try {
        const response = await shopAPI.getOrder(orderId);
        setOrder(response.data);
      } catch (err) {
        console.error('Error loading order:', err);
        setError('No se pudo cargar la información del pedido');
      } finally {
        setLoading(false);
      }
    };
    
    loadOrder();
  }, [orderId]);
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#7ED957]" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
      <Card className="bg-gray-800 border-gray-700 max-w-md w-full">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto mb-4 w-20 h-20 bg-[#7ED957]/20 rounded-full flex items-center justify-center">
            <CheckCircle2 className="w-12 h-12 text-[#7ED957]" />
          </div>
          <CardTitle className="text-2xl text-white">¡Pago exitoso!</CardTitle>
          <CardDescription className="text-gray-400">
            Tu pedido ha sido confirmado
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-6">
          {order && (
            <div className="bg-gray-700/50 rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-gray-400">Número de orden:</span>
                <span className="text-white font-mono text-sm">{order.id?.slice(0, 8)}...</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-400">Estado:</span>
                <span className="px-2 py-1 bg-[#7ED957]/20 text-[#7ED957] rounded-full text-sm">
                  {order.status === 'pagado' ? 'Pagado' : order.status}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-400">Total:</span>
                <span className="text-white font-bold">${order.total_amount?.toLocaleString()} MXN</span>
              </div>
              {order.items?.map((item, idx) => (
                <div key={idx} className="flex items-center justify-between text-sm">
                  <span className="text-gray-400 flex items-center gap-2">
                    <Package className="w-4 h-4" />
                    {item.product_type === 'shelly_user' ? 'Botón Shelly Jugador' : 'Botón Shelly Club'} x{item.quantity}
                  </span>
                </div>
              ))}
            </div>
          )}
          
          <div className="text-center text-gray-400 text-sm">
            <p>Te enviaremos un correo con los detalles de tu pedido y la información de envío.</p>
          </div>
          
          <div className="flex flex-col gap-3">
            <Button
              onClick={() => navigate('/profile')}
              className="w-full bg-[#7ED957] hover:bg-[#6bc548] text-black font-bold"
            >
              <ShoppingBag className="w-4 h-4 mr-2" />
              Ver mis pedidos
            </Button>
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              className="w-full border-gray-600 text-gray-300 hover:bg-gray-700"
            >
              <Home className="w-4 h-4 mr-2" />
              Volver al inicio
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ShopSuccess;
